(() => {
  if (customElements.get('customer-reviews')) return;
  const stars = (element, rating) => {
    element.textContent = '★★★★★';
    element.style.setProperty('--rating-width', `${rating / 5 * 100}%`);
  };
  customElements.define('customer-reviews', class extends HTMLElement {
    async connectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      const controller = this.controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(this.dataset.endpoint, {credentials:'omit', signal:controller.signal});
        if (!response.ok) throw new Error('Reviews unavailable');
        const data = await response.json();
        if (!this.isConnected || controller.signal.aborted) return;
        if (!Number.isInteger(data.rating_count) || data.rating_count < 1 ||
            !Number.isFinite(data.average_rating) || data.average_rating < 1 || data.average_rating > 5 ||
            !Array.isArray(data.reviews)) return;
        this.querySelector('[data-review-score]').textContent = data.average_rating.toFixed(1);
        stars(this.querySelector('[data-review-stars]'), data.average_rating);
        this.querySelector('[data-review-count]').textContent = `Based on ${data.rating_count} customer rating${data.rating_count === 1 ? '' : 's'}`;
        const reviews = data.reviews.filter(review => typeof review.name === 'string' &&
          typeof review.text === 'string' && review.text.trim() && Number.isInteger(review.rating) &&
          review.rating >= 1 && review.rating <= 5);
        // Shuffle once; never rearrange cards while someone is reading.
        for (let i = reviews.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [reviews[i], reviews[j]] = [reviews[j], reviews[i]];
        }
        const cards = reviews.map(review => {
          const card = document.createElement('figure');
          card.className = 'customer-reviews__card';
          const rating = document.createElement('div');
          rating.className = 'customer-reviews__stars';
          rating.setAttribute('role','img');
          rating.setAttribute('aria-label',`${review.rating} out of 5 stars`);
          stars(rating, review.rating);
          const quote = document.createElement('blockquote');
          quote.textContent = review.text;
          const name = document.createElement('figcaption');
          name.textContent = review.name;
          card.append(rating, quote, name);
          if (typeof review.product === 'string' && review.product.trim()) {
            const product = document.createElement('p');
            product.className = 'customer-reviews__product';
            product.textContent = `Model: ${review.product.trim()}`;
            card.append(product);
          }
          return card;
        });
        const track = this.querySelector('[data-review-cards]');
        track.replaceChildren(...cards);
        this.hidden = false;
        const previous = this.querySelector('[data-review-previous]');
        const next = this.querySelector('[data-review-next]');
        const controls = this.querySelector('[data-review-controls]');
        const status = this.querySelector('[data-review-position]');
        const stride = () => cards[0]?.getBoundingClientRect().width + 24 || 1;
        const update = () => {
          const max = track.scrollWidth - track.clientWidth;
          controls.hidden = max < 2;
          previous.disabled = track.scrollLeft < 2;
          next.disabled = track.scrollLeft >= max - 2;
          const first = Math.round(track.scrollLeft / stride()) + 1;
          const last = Math.min(cards.length, first + Math.round((track.clientWidth + 24) / stride()) - 1);
          status.textContent = `${first === last ? first : first + '–' + last} of ${cards.length}`;
        };
        const scroll = left => track.scrollTo({left, behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
        previous.onclick = () => scroll(track.scrollLeft - stride());
        next.onclick = () => scroll(track.scrollLeft + stride());
        track.addEventListener('scroll', update, {passive:true,signal:controller.signal});
        track.addEventListener('keydown', event => {
          if (event.target !== track) return;
          if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          event.preventDefault();
          scroll(event.key === 'Home' ? 0 : event.key === 'End' ? track.scrollWidth :
            track.scrollLeft + (event.key === 'ArrowRight' ? stride() : -stride()));
        }, {signal:controller.signal});
        this.resizeObserver = new ResizeObserver(update);
        this.resizeObserver.observe(track);
        update();
      } catch { /* Keep the homepage usable when reviews are unavailable. */ }
      finally { clearTimeout(timeout); }
    }
    disconnectedCallback() { this.controller?.abort(); this.resizeObserver?.disconnect(); }
  });
})();
